import DataFolder from "./DataFolder/DataFolder";
import SearchBar from "../Shared/SearchBar/SearchBar";
import Paper from "@mui/material/Paper";
import "./DataVisualisation.css";
import { useCallback, useEffect, useState } from "react";
import { getAllSamplesData, getSampleById } from "../../controllers/SamplesController";
import { getOrganizations, getOrganizationSamples } from "../../controllers/OrganizationController";
import { getExcursionsData } from "../../controllers/ExcursionController";
import DataList from "./DataList/DataList";
import { Divider, Typography } from "@mui/material";
import DataDetailsDrawer from "./DataDetailsDrawer/DataDetailsDrawer";
import SelectOption from "./SelectOption/SelectOption";

const FilterOptions = {
  "Years": { type: "range", name: "Années", possibleValues: [2010, new Date().getFullYear()], description: "Sélectionnez une plage d'années pour filtrer les données." },
  "Transect": { type: "range", name: "Longueur transecte", possibleValues: [10, 100], description: "Sélectionnez une plage de longueurs pour filtrer les données." }
};

const FiltersMapping = {
  "Années": "years",
  "Longueur transecte": "transect"
};

const viewOptions = [
  { name: "Organisations", value: "organizations" },
  { name: "Prélèvements", value: "samples" },
]

function DataVisualisation() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState("");

  const [showFoldersView, setShowFoldersView] = useState(true);
  const [showNoData, setShowNoData] = useState(true);

  const [currentSearch, setCurrentSearch] = useState("");
  const [currentFilters, setCurrentFilters] = useState([]);

  const [openDetails, setOpenDetails] = useState(false);
  const [selectedSample, setSelectedSample] = useState(null);

  const [organizationsList, setOrganizationsList] = useState([]);
  const [organizationsWithSamples, setOrganizationsWithSamples] = useState([]);
  const [excursions, setExcursions] = useState([]);

  const [selectedViewOption, setSelectedViewOption] = useState(viewOptions[0].value);
  const [searchOptions, setSearchOptions] = useState([]);


  useEffect(() => {
    getAllSamplesData().then((response) => {
      if (response) {
        setData(response);
        setShowNoData(false);
      }
    });

    getOrganizations().then((response) => {
      setOrganizationsList(response);
    });

    getExcursionsData().then((response) => {
      setExcursions(response);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchOrganizationSamples = useCallback((title, samplesId) => {
    const samples = samplesId.map((id) => {
      return getSampleById(id).then((response) => {
        return response;
      });
    });

    Promise.all(samples).then((samples) => {
      const filteredSamples = samples.filter((sample) => sample !== null);
      setOrganizationsWithSamples((prev) => {
        if (prev.some((org) => org.name === title)) {
          return prev;
        }
        const updatedOrgs = [...prev, { name: title, data: filteredSamples }];
        return updatedOrgs;
      });
    });
  }, []);

  const fetchAllSamples = useCallback((queryParams) => {
    getAllSamplesData(queryParams).then((response) => {
      setData(response);
      setFilteredData(response);
      setShowNoData(false);
    });

  }, []);

  useEffect(() => {
    if (organizationsList.length > 0) {
      const orgData = organizationsList.map((org) => {
        return getOrganizationSamples(org.id).then((response) => {
          if (response && response.length > 0) {
            return { name: org.name, data: response };
          }
          return null;
        });
      });
      Promise.all(orgData).then((orgs) => {
        const filteredOrgs = orgs.filter((org) => org !== null);
        filteredOrgs.forEach((org) => {
          fetchOrganizationSamples(org.name, org.data);
        });
        setSearchOptions(() => [
          ...filteredOrgs.map((org) => {
            return org.name;
          })
        ]);
        setShowNoData(false);
        return filteredOrgs;
      });

      setFolders(organizationsWithSamples)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationsList, fetchOrganizationSamples]);

  useEffect(() => {
    console.log("selected view use effect", selectedViewOption);
    if (selectedViewOption === "organizations") {
      setShowFoldersView(true);
      setSearchOptions(() => [
        ...organizationsWithSamples.map((org) => {
          return org.name;
        })
      ]);
    } else {
      setSearchOptions(() => [
        ...excursions.map((org) => {
          return org.name;
        })
      ]);
      setShowFoldersView(false);
      setFilteredData(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewOption]);

  const applyFilters = useCallback((filters, newFolders) => {
    if (filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.name === "Années") {
          newFolders = newFolders.map((folder => {
            return {
              ...folder, data: folder.data.filter((item) => {
                return item.sample_date && new Date(item.sample_date).getFullYear() >= filter.value[0] && new Date(item.sample_date).getFullYear() <= filter.value[1];
              })
            };
          }));
        } else if (filter.name === "Longueur transecte") {
          newFolders = newFolders.map((folder => {
            return {
              ...folder, data: folder.data.filter((item) => {
                return item.transect_length && item.transect_length >= filter.value[0] && item.transect_length <= filter.value[1];
              })
            };
          }));
        }
      });
    }

    setFolders(newFolders);

    if (!showFoldersView) {
      setFilteredData(newFolders.find(folder => folder.name === currentFolder)?.data);
    }

    if (newFolders.every((folder) => folder.data.length === 0)) {
      setShowNoData(true);
    }
  }, [currentFolder, showFoldersView]);

  const search = useCallback((searchValue, newFolders) => {
    if (searchValue !== "") {
      if (showFoldersView && selectedViewOption === "organizations") {
        newFolders = newFolders.filter((folder) => {
          return folder.name.toLowerCase().includes(searchValue.toLowerCase());
        })
      }
    }

    setFolders(newFolders);

    if (!showFoldersView) {
      setFilteredData(newFolders.find(folder => folder.name === currentFolder)?.data);
    }
    if (newFolders.every((folder) => folder.data.length === 0)) {
      setShowNoData(true);
    }
  }, [currentFolder, selectedViewOption, showFoldersView]);

  useEffect(() => {
    const newFolders = organizationsWithSamples;

    if (selectedViewOption === viewOptions[1].value) {
      let queryParam = "";
      if (currentFilters.length > 0) {
        queryParam = currentFilters.map(filter => `${FiltersMapping[filter.name]}=${filter.value}`).join("&");
      }
      if (currentSearch !== "") {
        const searchedId = excursions.find((excursion) => excursion.name.toLowerCase().includes(currentSearch.toLowerCase()))?.id;
        if (searchedId) {
          queryParam += `&excursion_id=${searchedId}`;
        }
      }
      fetchAllSamples(queryParam);

      return;

    }

    if (currentFilters.length === 0 && currentSearch === "") {
      setFolders(newFolders);
      if (currentFolder) {
        setFilteredData(newFolders.find(folder => folder.name === currentFolder)?.data);
      }
      setShowNoData(false);
      return;
    }
    if (currentFilters.length > 0) {
      applyFilters(currentFilters, newFolders);
    }
    if (currentSearch !== "") {
      search(currentSearch, newFolders);
    }

  }, [
    currentSearch,
    currentFilters,
    selectedViewOption,
    currentFolder,
    organizationsWithSamples,
    excursions,
    applyFilters,
    search,
    fetchAllSamples
  ]);

  const openFolder = (title) => {
    setCurrentFolder(title);
    setShowFoldersView(false);
  };

  const goBack = () => {
    setShowFoldersView(true);
    setSelectedViewOption(viewOptions[0].value);
    setCurrentFolder("");
  };

  const handleOpenDetailsDrawer = (sample) => {
    setSelectedSample(sample);
    setOpenDetails(true);
  };

  const handleCloseDetailsDrawer = () => {
    setSelectedSample(null);
    setOpenDetails(false);
  };

  const handleChangeView = (event) => {
    setSelectedViewOption(event.target.value);
  }

  const updateFilters = (filters) => {
    setCurrentFilters(filters);
  }
  const updateSearch = (searchValue) => {
    setCurrentSearch(searchValue);
  };

  return (
    <div>
      <h1 className="page-title">Visualisation des prélèvements</h1>
      <SearchBar
        autocomplete
        dataView
        search={updateSearch}
        applyFilters={updateFilters}
        filterOptions={FilterOptions}
        searchOptions={searchOptions}
        disableAutocomplete={!showFoldersView && selectedViewOption === "organizations"}
      />
      <Paper className="paper" elevation={3} display="flex">
        <div className="header-container">
          <SelectOption selected={selectedViewOption} options={viewOptions} onChange={handleChangeView} />
        </div>
        <Divider className="divider" />
        <div className="data-container">

          {!showNoData && showFoldersView && folders.length > 0 &&
            <div className="folders">
              {folders.map((org) => {
                if (org.data.length === 0) return null;
                const folder = organizationsList.find((o) => o.name === org.name)
                return (
                  <DataFolder
                    key={folder.id}
                    title={folder.name}
                    image={folder.logo_base64}
                    openFolder={openFolder}
                  />
                );
              })}
            </div>}
          {!showNoData && !showFoldersView && (
            <DataList
              className="data-list"
              data={filteredData}
              goBack={goBack}
              openDetails={handleOpenDetailsDrawer}
              selectedRow={selectedSample}
              excursion={currentSearch !== "" ? currentSearch : null}
            />
          )}
        </div>
        {showNoData && (
          <div>
            <Typography variant="h5" component="div">
              Aucun prélèvement trouvé
            </Typography>
          </div>
        )}
      </Paper>
      <DataDetailsDrawer
        open={openDetails}
        sampleData={selectedSample}
        onClose={handleCloseDetailsDrawer}
      />
    </div>
  );
}

export default DataVisualisation;
